import React, { useState, useEffect } from "react";
import axios from "axios";
import dayjs from "dayjs";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TablePagination,
  Grid,
  Card,
  CardContent,
  Alert,
  Divider,
} from "@mui/material";
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { 
  getAllowedLabTypes, 
  getLabTypeName, 
  getInitialLabTypeId,
  getFirstLabTypeId, 
  shouldDisableDropdown,
  LAB_TYPES 
} from "../../../../utils/labTypeFilter";

const PTOtherMaster = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
  
  // Get filtered lab types using utility
  const filteredLabTypes = getAllowedLabTypes(LAB_TYPES);

  // Helper function to get lab type name by ID
  const getLabTypeNameLocal = (labTypeId) => {
    return getLabTypeName(labTypeId, filteredLabTypes);
  };

  const [formData, setFormData] = useState({
    id: null,
    LabTypeId: "",
    ParticipationIn: "PT/EQA (Others)",
    Name: "",
    isactive: 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [ptOthers, setPTOthers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterLabType, setFilterLabType] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  // Bulk upload dialog state
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkLabType, setBulkLabType] = useState("");
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkError, setBulkError] = useState("");
  const [bulkSuccess, setBulkSuccess] = useState("");

  // Download Excel template
  const handleDownloadTemplate = () => {
    if (!bulkLabType) {
      setBulkError("Please select Lab Type to download template");
      return;
    }
    const header = [["Name"]];
    const ws = XLSX.utils.aoa_to_sheet(header);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    const lab = filteredLabTypes.find((l) => l.id === bulkLabType)?.name || "lab";
    const fileName = `PTOtherTemplate_${lab}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Parse Excel or CSV file
  const parseFile = async (file) => {
    const required = ["Name"];
    const ext = file.name.split(".").pop().toLowerCase();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (evt) => {
        if (ext === "xlsx" || ext === "xls") {
          try {
            const data = new Uint8Array(evt.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            if (!json.length) return reject(new Error("File is empty"));
            const headers = json[0].map((h) => (h || "").toString().trim());
            const missing = required.filter((r) => !headers.includes(r));
            if (missing.length)
              return reject(
                new Error("Missing headers: " + missing.join(", ")),
              );
            const rows = json
              .slice(1)
              .map((rowArr) => {
                const obj = {};
                headers.forEach((h, idx) => {
                  obj[h] = rowArr[idx] ? rowArr[idx].toString().trim() : "";
                });
                return obj;
              })
              .filter((r) => r.Name);
            resolve(rows);
          } catch (e) {
            reject(new Error("Invalid Excel file"));
          }
        } else {
          // CSV logic
          const text = evt.target.result;
          const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
          if (lines.length === 0) return reject(new Error("File is empty"));
          const headers = lines[0].split(",").map((h) => h.trim());
          const missing = required.filter((r) => !headers.includes(r));
          if (missing.length)
            return reject(new Error("Missing headers: " + missing.join(", ")));
          const rows = lines
            .slice(1)
            .map((line) => {
              const cols = line.split(",");
              const obj = {};
              headers.forEach((h, idx) => {
                obj[h] = cols[idx] ? cols[idx].trim() : "";
              });
              return obj;
            })
            .filter((r) => r.Name);
          resolve(rows);
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      if (ext === "xlsx" || ext === "xls") {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsText(file);
      }
    });
  };

  useEffect(() => {
    fetchPTOthers();
  }, []);

  // Get labTypeId from localStorage and set in form data (same as ScrutinyRemarksManagement)
  useEffect(() => {
    const initialLabTypeId = getInitialLabTypeId();
    const firstLabTypeId = getFirstLabTypeId(); // Get first available lab type for filter default
    
    console.log('Initialization - initialLabTypeId:', initialLabTypeId);
    console.log('Initialization - firstLabTypeId for filter:', firstLabTypeId);
    
    if (initialLabTypeId) {
      const labTypeName = getLabTypeNameLocal(initialLabTypeId);
      console.log(`Initial Lab Type ID: ${initialLabTypeId}, Lab Type: ${labTypeName}`);
      
      setFormData(prev => ({ 
        ...prev, 
        LabTypeId: initialLabTypeId 
      }));
      // Also set the bulk lab type
      setBulkLabType(initialLabTypeId);
    }
    
    // Set filter to first available lab type (instead of empty/all)
    if (firstLabTypeId) {
      setFilterLabType(firstLabTypeId);
      console.log('Set labTypeFilter to first available:', firstLabTypeId);
    } else {
      console.log('No lab type IDs found for filter');
    }
  }, []);

  const fetchPTOthers = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin-pt-master/pt-others`,
      );
      if (res.data.status === "success") {
        setPTOthers(res.data.data || []);
      } else {
        setError(res.data.message || "Failed to fetch PT Others");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching PT Others");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.LabTypeId || !formData.Name) {
      setError("Lab Type and Name are required");
      return;
    }
    setError("");
    setLoading(true);
    try {
      if (formData.id) {
        // Update
        await axios.put(
          `${process.env.REACT_APP_API_URL}/admin-pt-master/pt-others/${formData.id}`,
          {
            LabTypeId: formData.LabTypeId,
            ParticipationIn: formData.ParticipationIn,
            Name: formData.Name,
          },
        );
        setSuccessMessage("PT Other updated successfully!");
      } else {
        // Create
        await axios.post(
          `${process.env.REACT_APP_API_URL}/admin-pt-master/pt-others`,
          {
            LabTypeId: formData.LabTypeId,
            ParticipationIn: formData.ParticipationIn,
            Name: formData.Name,
          },
        );
        setSuccessMessage("PT Other inserted successfully!");
      }
      fetchPTOthers();
      setTimeout(() => setSuccessMessage(""), 3000);
      handleReset();
    } catch (err) {
      setError(err.response?.data?.message || "Error saving PT Other");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    setFormData({
      id: row.ID,
      LabTypeId: row.LabTypeId,
      ParticipationIn: row.ParticipationIn,
      Name: row.Name,
      isactive: row.IsActive,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    setError("");
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/admin-pt-master/pt-others/${deleteId}`,
      );
      setSuccessMessage("PT Other deleted successfully");
      fetchPTOthers();
    } catch (err) {
      setError(err.response?.data?.message || "Error deleting PT Other");
    } finally {
      setLoading(false);
      setTimeout(() => setSuccessMessage(""), 2000);
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  const handleReset = () => {
    const storedLabTypeId = localStorage.getItem('labTypeId');
    setFormData({
      id: null,
      LabTypeId: storedLabTypeId ? Number(storedLabTypeId) : "",
      ParticipationIn: "PT/EQA (Others)",
      Name: "",
      isactive: 1,
    });
    setError("");
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f5f5f5", p: 1 }}>
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        {/* Gradient Header */}
        <Paper
          elevation={3}
          sx={{
            p: 4,
            mb: 3,
            background: "linear-gradient(135deg, #43cea2 0%, #185a9d 100%)",
            color: "white",
            textAlign: "center",
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            <AssignmentIcon
              sx={{ fontSize: "2rem", mr: 2, verticalAlign: "middle" }}
            />
            PT Other Master
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            Manage PT Other records efficiently
          </Typography>
        </Paper>
        {successMessage && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccessMessage("")}
          >
            {successMessage}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {/* Form Card */}
        <Card elevation={3} sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <AssignmentIcon
                sx={{ fontSize: "2rem", color: "#1976d2", mr: 1 }}
              />
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "#1976d2" }}
              >
                Add / Edit PT Other
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Box
              sx={{
                p: 2,
                backgroundColor: "#f8f9fa",
                borderRadius: 2,
                border: "1px solid #e9ecef",
                mb: 2,
              }}
            >
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Select
                      fullWidth
                      name="LabTypeId"
                      value={formData.LabTypeId}
                      onChange={handleInputChange}
                      displayEmpty
                      required
                      disabled={shouldDisableDropdown(filteredLabTypes)}
                      variant="outlined"
                    >
                      <MenuItem value="" disabled>
                        <em>--Select Lab Type--</em>
                      </MenuItem>
                      {filteredLabTypes.map((lab) => (
                        <MenuItem key={lab.id} value={lab.id}>
                          {lab.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Participation In"
                      name="ParticipationIn"
                      value={formData.ParticipationIn}
                      disabled
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Name"
                      name="Name"
                      value={formData.Name}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                      placeholder="e.g., test abc"
                    />
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    mt: 4,
                    display: "flex",
                    gap: 3,
                    justifyContent: "center",
                  }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={loading}
                    sx={{ backgroundColor: "#1976d2" }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    startIcon={<RefreshIcon />}
                    disabled={loading}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<UploadFileIcon />}
                    onClick={() => setBulkDialogOpen(true)}
                    sx={{ minWidth: 160 }}
                  >
                    Bulk Upload
                  </Button>
                </Box>
              </form>
            </Box>
          </CardContent>
        </Card>
        {/* Table Card */}
        <Card elevation={3} sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <AssignmentIcon
                sx={{ fontSize: "2rem", color: "#1976d2", mr: 1 }}
              />
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "#1976d2" }}
              >
                PT Other List
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <Select
                fullWidth
                displayEmpty
                disabled={shouldDisableDropdown(filteredLabTypes)}
                value={filterLabType}
                onChange={(e) => {
                  setFilterLabType(e.target.value);
                  setPage(0);
                }}
                renderValue={(selected) => {
                  if (!selected) return <em>--All Lab Types--</em>;
                  const labType = filteredLabTypes.find((l) => l.id === selected);
                  return labType?.name || 'Unknown';
                }}
              >
                <MenuItem value="">
                  <em>--All Lab Types--</em>
                </MenuItem>
                {filteredLabTypes.map((lab) => (
                  <MenuItem key={lab.id} value={lab.id}>
                    {lab.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
          {/* Bulk Upload Dialog */}
          <Dialog
            open={bulkDialogOpen}
            onClose={() => setBulkDialogOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>Bulk Upload PT Others</DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 1, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Lab Type
                </Typography>
                <Select
                  fullWidth
                  value={bulkLabType}
                  onChange={(e) => setBulkLabType(e.target.value)}
                  displayEmpty
                  disabled={shouldDisableDropdown(filteredLabTypes)}
                  required
                >
                  <MenuItem value="" disabled>
                    <em>--Select Lab Type--</em>
                  </MenuItem>
                  {filteredLabTypes.map((lab) => (
                    <MenuItem key={lab.id} value={lab.id}>
                      {lab.name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Grid
                container
                spacing={1}
                alignItems="center"
                sx={{ mt: 2, mb: 2 }}
              >
                <Grid item xs={7}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Select Excel/CSV File
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <Button
                    variant="outlined"
                    onClick={handleDownloadTemplate}
                    fullWidth
                  >
                    Download Template
                  </Button>
                </Grid>
              </Grid>
              <input
                type="file"
                accept=".csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={(e) => setBulkFile(e.target.files[0])}
                style={{ marginTop: 8 }}
              />
              {bulkFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {bulkFile.name}
                </Typography>
              )}
              {bulkError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {bulkError}
                </Alert>
              )}
              {bulkSuccess && (
                <Alert severity="success" sx={{ mb: 1 }}>
                  {bulkSuccess}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setBulkDialogOpen(false)}
                disabled={bulkUploading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<UploadFileIcon />}
                disabled={bulkUploading || !bulkLabType || !bulkFile}
                onClick={async () => {
                  setBulkError("");
                  setBulkSuccess("");
                  if (!bulkLabType || !bulkFile) {
                    setBulkError("Please select Lab Type and file.");
                    return;
                  }
                  setBulkUploading(true);
                  try {
                    const rows = await parseFile(bulkFile);
                    let successCount = 0;
                    let failCount = 0;
                    for (const row of rows) {
                      try {
                        await axios.post(
                          `${process.env.REACT_APP_API_URL}/admin-pt-master/pt-others`,
                          {
                            LabTypeId: bulkLabType,
                            ParticipationIn: "PT/EQA (Others)",
                            Name: row.Name,
                          },
                        );
                        successCount++;
                      } catch (err) {
                        failCount++;
                      }
                    }
                    setBulkSuccess(
                      `Uploaded: ${successCount}, Failed: ${failCount}`,
                    );
                    fetchPTOthers();
                  } catch (err) {
                    setBulkError(err.message);
                  } finally {
                    setBulkUploading(false);
                  }
                }}
              >
                {bulkUploading ? "Uploading..." : "Upload"}
              </Button>
            </DialogActions>
          </Dialog>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              Loading...
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell>
                        <strong>S.no</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Lab Type</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Participation In</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Name</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Created On</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Status</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Action</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ptOthers.filter(
                      (row) =>
                        !filterLabType ||
                        String(row.LabTypeId) === String(filterLabType),
                    ).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography color="textSecondary">
                            No PT Other data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      ptOthers
                        .filter(
                          (row) =>
                            !filterLabType ||
                            String(row.LabTypeId) === String(filterLabType),
                        )
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage,
                        )
                        .map((row, idx) => {
                          const labType = filteredLabTypes.find(
                            (l) => String(l.id) === String(row.LabTypeId),
                          );
                          return (
                            <TableRow key={row.ID}>
                              <TableCell>
                                {page * rowsPerPage + idx + 1}
                              </TableCell>
                              <TableCell>
                                {labType?.name || row.LabTypeId}
                              </TableCell>
                              <TableCell>{row.ParticipationIn}</TableCell>
                              <TableCell>{row.Name}</TableCell>
                              <TableCell>
                                {row.CreatedOn
                                  ? dayjs(row.CreatedOn).isValid()
                                    ? dayjs(row.CreatedOn).format(
                                        "YYYY-MM-DD HH:mm:ss",
                                      )
                                    : row.CreatedOn
                                  : ""}
                              </TableCell>
                              <TableCell>
                                <span
                                  style={{
                                    padding: "4px 8px",
                                    borderRadius: "4px",
                                    backgroundColor: row.IsActive
                                      ? "#c8e6c9"
                                      : "#ffcccc",
                                    color: row.IsActive ? "#2e7d32" : "#c62828",
                                  }}
                                >
                                  {row.IsActive ? "Active" : "Inactive"}
                                </span>
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEdit(row)}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteClick(row.ID)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={
                  ptOthers.filter(
                    (row) =>
                      !filterLabType ||
                      String(row.LabTypeId) === String(filterLabType),
                  ).length
                }
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                sx={{ borderTop: "1px solid #e0e0e0" }}
              />
            </>
          )}
        </Card>
        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this PT Other?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={loading}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default PTOtherMaster;
